import {
  Workplace,
  Training,
  LeaveRule,
  Department,
  CustomFieldData,
} from '@/modules/people/types/people-setup';
import { PayRuleOverride } from '@/modules/people/types/people';
import {
  JuniorRate,
  PayDetailSalaryDistribuitionConfig,
  PayPeriod,
  PayRule,
  SalaryPayRuleObject,
} from '@/modules/people/types/employment';
import {
  FormSection,
  StateEmploymentEmployeeAgreement,
} from '@/modules/people/store/employment';
import { ISOString } from '@/types/date-types';
import { ContractStatuses } from '@/modules/people/modules/pay-rates/definitions';
import { PayrollAccess } from '@/types/classic';

type BooleanPHP = '0' | '1' | 1 | 0;

export const INVITATION_STATUS = {
  PENDING: 0,
  ACCEPTED: 1,
  DECLINED: 2,
  NOT_INVITED: 3,
} as const;

export type InvitationStatus =
  (typeof INVITATION_STATUS)[keyof typeof INVITATION_STATUS];

export type _DPMetaData = {
  System: string;
  CreatorInfo: Record<string, unknown>;
};

export interface EmployeeTrainingFile {
  Id: number;
}

export interface EmployeeLeaveAccrual {
  Id: number | string;
  Hours: number;
  Days: number;
  LeaveRuleId: number;
  HoursFuture: number;
  hoursToHuman?: number;
}

export interface EmployeeContractPayRuleOperationalUnit {
  workAreas: string[];
  payRuleIds: number[];
  comment: string;
}

export interface EmployeeContract {
  Id: number;
  Code: string;
  Name: string;
  Description: string;
  EmploymentBasis: number;
  EmploymentCategory: number;
  EmploymentStatus: number | undefined;
  EmploymentCondition: null | number;
  BasePayRule: number;
  BasePayRuleObject?: Record<string, number | string>;
  StressProfile: null | number; // StressProfile.Id
  StartDate: null | string;
  EndDate: null | string;
  PeriodType: number;
  File: null; // | EmployeeContractFile ??
  StrictLeaveApproval: null | number;
  Award: null | string;
  EmploymentSubType: null | number;
  AwardStartDate: null | string;
  Creator: number;
  Created: string;
  Modified: string;
  PpId: null | string;
  CountryId: null | number;
  _DPMetaData: _DPMetaData;
  ThirdPartySync: string;
  PayRuleArray: PayRule[];
  PayRuleOperationalUnitArray: EmployeeContractPayRuleOperationalUnit[];
  LeaveRuleArray: LeaveRule[];
  MigrateTo: {
    Code: string;
    IsMajorVersion: boolean;
  };
  FileName?: string;
  IsPayPolicy?: boolean;
  // TODO: verify how this gets injected here
  payRules: EmployeeAgreementContractPayRule[];
  TermId?: string;
  ContractStatus?: ContractStatuses;
  Status?: ContractStatuses;
  Levels: Array<ContractLevel>;
  IsSalaryContract: boolean;
  DefaultBaseRate?: number | null;
  AdultDefaultBaseRate?: number | null;
  DeputecEmploymentContractJuniorRates?: JuniorRate[];
}

export interface EmployeeAgreementPayPoint {
  Id: number;
  Portfolio: null | string;
  Code: string;
  Active: boolean;
  ParentCompany: number;
  CompanyName: string;
  TradingName: null | string;
  BusinessNumber: null | string;
  CompanyNumber: null | string;
  IsWorkplace: boolean;
  IsPayrollEntity: boolean;
  PayrollExportCode: null | string;
  Address: number;
  Contact: number;
  Creator: number;
  Created: string;
  Modified: string;
}

export interface EmployeeAgreementContractPayRule {
  Id: number;
  PayTitle: string;
  RemunerationType: number;
  RemunerationBy: number;
  AnnualSalary: null | number;
  HourlyRate: number;
  IsMultiplier: boolean;
  MultiplierValue: null | number;
  MultiplierBaseRate: null | number;
  MinimumType: number;
  MaximumType: number;
  MinimumValue: null | number;
  MaximumValue: null | number;
  MinimumShiftLength: null | number;
  MaximumShiftLength: null | number;
  AdvancedCalculation: null | number;
  IsExported: boolean;
  UnitValue: null | number;
  Schedule: number;
  RecommendWith: number;
  DexmlScript: null | string;
  DexmlScriptParam: null | string;
  PeriodType: null | string;
  PayPortionRule: number;
  PayrollCategory: string;
  Comment: string;
  RateType: null | string;
  RateTag: null | string;
  Creator: number;
  Created: string;
  Modified: string;
  CtId: null | string;
  CrId: null | string;
  Config: null | string;
  _DPMetaData: _DPMetaData;
}

// TODO: unify with EmployeeContract, same type?
export interface EmployeeAgreementContract {
  Id: number;
  Code: string;
  Name: string;
  Description: string;
  EmploymentBasis: number;
  EmploymentCategory: number;
  EmploymentStatus: number | undefined;
  EmploymentCondition: null | number;
  BasePayRule: number;
  StressProfile: null | number; // StressProfile.Id
  StartDate: null | string;
  EndDate: null | string;
  PeriodType: number;
  File: null; // EmployeeAgreementContractFile ??
  StrictLeaveApproval: null | string | undefined | number;
  Award: null | string;
  EmploymentSubType: null | string | undefined | number;
  AwardStartDate: null | string;
  Creator: number;
  Created: string;
  Modified: string;
  PpId: null | string;
  CountryId: null | number;
  ThirdPartySync: string;
  PayRuleArray: Partial<EmployeeAgreementContractPayRule> | Partial<PayRule>[];
  PayRuleOverrides: Array<PayRuleOverride>;
  BasePayRuleObject: Partial<PayRule>;
  Levels: ContractLevel[];
  DeputecEmploymentContractJuniorRates?: JuniorRate[];
  _DPMetaData: _DPMetaData;
}

export interface EmployeeAgreement {
  Id: number;
  EmployeeId: number;
  EffectiveDate?: string;
  PayPoint: number;
  EmpType: number;
  CompanyName: null | string;
  Active: boolean | BooleanPHP; // BooleanPHP used to save
  StartDate: string;
  EndDate: null | string;
  Contract: number;
  SalaryPayRule: number;
  ContractFile: number;
  PayrollId: string;
  PayPeriod: number;
  HistoryId: number;
  Creator: number;
  Created: string;
  Modified: string;
  BaseRate: null | number;
  Config: null | string;
  PayPointObject: Partial<EmployeeAgreementPayPoint>;
  ContractObject: Partial<EmployeeAgreementContract> | undefined;
  PayPeriodObject: Partial<PayPeriod>;
  SalaryDistributionConfig: Partial<PayDetailSalaryDistribuitionConfig>;
  SalaryPayRuleObject: Partial<SalaryPayRuleObject>;
  SalaryDistributions: number[];
  CustomTermIdentifier: string;
  IsCustomTermFromTemplate: boolean;
  PayCalendarId?: number | null;
  LevelId?: number;
  AnnualSalary?: number;
  DefaultBaseRate?: number | null;
  // The contract's unreduced base rate, captured by setEmployeeBaseRates before it
  // overwrites the pay-rule copies with the agreement's saved (possibly junior) rate.
  AdultDefaultBaseRate?: number | null;
  CompanyIds?: number[];
  JuniorRateId?: number | null;
  OverrideWithAdultRate?: boolean | null;
}

export type EmployeeAddress = {
  Id: number;
  ContactName: null | string;
  UnitNo: null | string;
  StreetNo: null | string;
  SuiteNo: null | string;
  PoBox: null | string;
  Street1: string;
  Street2: null | string;
  City: string;
  State: string;
  Postcode: string;
  Country: number;
  Phone: null;
  Notes: null;
  Format: number;
  Saved: boolean;
  Creator: number;
  Created: string; // "2023-11-16T17:36:43+11:00",
  Modified: string; // '2023-11-16T17:36:43+11:00';
  Print: string;
};

export default interface Employee {
  Id: number;
  AgreementCount: number;
  AgreementEffectiveDateArray: Array<string>;
  AgreementCurrentEffectiveDate: string;
  Company: number;
  FirstName: null | string;
  LastName: null | string;
  DisplayName: null | string;
  OtherName: null | string;
  Salutation: null | string;
  MainAddress: number;
  PostalAddress: null | string;
  Contact: number;
  EmergencyAddress: number;
  DateOfBirth: string;
  Gender: number;
  Pronouns: null | string | number;
  CustomPronouns: null | string;
  Photo: null | number;
  UserId: number;
  JobAppId: null | string;
  Active: boolean;
  StartDate: ISOString;
  TerminationDate: null | string;
  EmploymentEndDate?: null | ISOString;
  EmploymentEndReason?: null | string;
  EmploymentEndSentiment?: null | string;
  EmploymentEndComment?: null | string;
  StressProfile: number; // StressProfile.Id
  LevelId: number;
  Position: null | string;
  /**
   * Job title library assignment, resolved by the server as a pair. Both keys
   * are absent while the title is unknown (the surface flag is off, or svc-hr
   * could not be read) and present and null while the employee holds no
   * title, so presence and value carry different answers.
   */
  JobTitleId?: null | string;
  JobTitle?: null | string;
  HigherDuty: null | string;
  Role: number;
  AllowAppraisal: boolean;
  HistoryId: number;
  CustomFieldData: null | string;
  OnboardingId: null | string;
  Creator: number;
  Created: string;
  Modified: string;
  _DPMetaData: _DPMetaData;
  IsSubordinate: boolean;
  WorkplaceArray: Workplace[];
  TrainingArray: Training[];
  TrainingFileArray: EmployeeTrainingFile[];
  LeaveAccrualArray: EmployeeLeaveAccrual[];
  PayrollId: string;
  PayCalendarId: number | null;
  Contract: EmployeeContract;
  SalaryDistributionConfig: null | number;
  Email: string;
  Mobile: string;
  PhotoLinkId: string;
  Img: string;
  Status: InvitationStatus;
  CanAskToShowPin: boolean;
  HasIPhoneToken: boolean;
  HasAndroidToken: boolean;
  HasEnabled2FA: boolean;
  AnalyticsID: string;
  UserName: string;
  AllowLogin: boolean;
  OnboardingStatus: number;
  EmployeeHourlyRate: number;
  Agreements: EmployeeAgreement[];
  PayrollAccess: PayrollAccess[];

  // these are added when editing the employee
  AvgHoursPerDay: number | null;
  EntitledLeaveRule: (string | number)[]; // TODO: align to just one type
  _address: EmployeeAddress;
  _emergencyAddress: EmployeeAddress;
  CustomField: CustomFieldData;
}

// TODO: adjust this type correctly as when saving it will have different properties
export type EmployeeApi = {
  Id: number | undefined;
  FirstName: string;
  LastName: string;
  DisplayName: string;
  Email: string;
  Mobile: string;
  Role: number | null;
  WorkplaceIdArray: Workplace['Id'][];
  TrainingRecordArray: EmployeeApiTraining[];
  StartDate: string | null;
  Gender: number;
  Pronouns: number | string | null;
  CustomPronouns: string | null;
  DateOfBirth: string | null;
  AvgHoursPerDay: string | number | null;
  EntitledLeaveRule: (string | number)[] | undefined;
  StressProfile: number | null;
  Position: string | null;
  /** Null is a real value: clearing the field unassigns. */
  JobTitleId?: string | null;
  Prefers: Department['Id'][];
  Manages: Employee['Id'][];
  sections: FormSection[];
  Agreements: Partial<StateEmploymentEmployeeAgreement>[] | null;
  Workplaces: Workplace[] | null;
};

export type EmployeeApiTraining = Pick<
  Training,
  | 'Id'
  | 'Module'
  | 'ExpiryDate'
  | 'File'
  | 'ModuleTitle'
  | 'TrainingDate'
  | 'Comment'
  | 'DownloadLink'
>;

export type ArchiveFailedEmployee = {
  employeeId: number | null;
  displayName: string | null;
  errors: string[];
};

export interface SalaryDistributionConfigArea {
  Active: boolean;
  Address: number | null;
  AddressObject: any[];
  Colour: string | null;
  Company: number;
  CompanyAddress: number;
  CompanyCode: string;
  CompanyName: string;
  Contact: string | null;
  Created: string; // ISO 8601 date string
  Creator: number;
  DailyRosterBudget: number | null;
  Id: number;
  Modified: string; // ISO 8601 date string
  OperationalUnitName: string;
  OperationalUnitType: string | null;
  ParentOperationalUnit: number;
  PayrollExportName: string | null;
  RosterActiveHoursSchedule: string | null;
  RosterSortOrder: number;
  ShowOnRoster: boolean;
  WorkType: string | null;
}

export interface ContractLevel {
  Id: number | string;
  Title: string;
  LevelKsuid: string;
  PayRules: Array<string>;
  ExportCode: string;
  BaseRate: number;
}

declare module '@/modules/people/types/employee' {}
